import React, { useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Monitor, Palette, Smartphone, Download, ClipboardList } from 'lucide-react';
import BrandingTab from './branding/BrandingTab';
import PendingConfigurationTab from './PendingConfigurationTab';
import { useStaffAccess } from '../../hooks/useStaffAccess';
import { LegacyFleetGate } from '../fleet/legacyRedirects';
import { prefersFleetMonitoring } from '../fleet/fleetAccess';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

type TabId = 'pending' | 'install' | 'branding' | 'fleet';

/** The three tabs BrandingTab already understands. It must never be handed a new id. */
const BRANDING_TABS = ['install', 'branding', 'fleet'] as const;
type BrandingTabId = (typeof BRANDING_TABS)[number];

const TABS: { id: TabId; label: string; icon: React.FC<any> }[] = [
  { id: 'pending', label: 'Pending configuration', icon: ClipboardList },
  { id: 'install', label: 'Install', icon: Download },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'fleet', label: 'Fleet', icon: Smartphone },
];

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

const VALID_TABS = TABS.map((t) => t.id);

/**
 * Catalog, Bundles, Offers and Orders used to live here as tabs. They aren't
 * device CONFIGS — catalog/bundle editing moved to the Inventory section,
 * Offers was unused (financing offers are created from the Sales page's
 * bundle cards now), and Orders already has a real home. Old `?tab=` links
 * (bookmarks, other pages not yet updated) redirect here rather than
 * rendering blank.
 */
const REMOVED_TAB_REDIRECT: Record<string, string> = {
  catalog: '/inventory/catalog',
  bundles: '/inventory/bundles-editor',
  offers: '/inventory/sales',
  orders: '/inventory/orders',
};

const PosConfigTab: React.FC = () => {
  // `?tab=` so other screens can deep-link here. `?unitId=` additionally
  // preselects a unit on the Install tab (from Pending configuration's
  // "Configure →", or a barcode scan elsewhere).
  const [searchParams, setSearchParams] = useSearchParams();
  // Fleet moved to /fleet-monitoring (Task 24). The tab disappears ONLY for a
  // user who can open the new page — a /pos-config-only role keeps it.
  const staffAccess = useStaffAccess();
  const fleetMoved = !staffAccess.loading && prefersFleetMonitoring(staffAccess);
  const fromUrl = searchParams.get('tab');

  if (fromUrl && REMOVED_TAB_REDIRECT[fromUrl]) {
    return <Navigate to={REMOVED_TAB_REDIRECT[fromUrl]} replace />;
  }

  const tab: TabId = fromUrl && (VALID_TABS as string[]).includes(fromUrl) ? (fromUrl as TabId) : 'pending';
  const setTab = useCallback((next: TabId) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    }, { replace: false });
  }, [setSearchParams]);

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      {/* Black header */}
      <header className="bg-black text-white px-4 md:px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gray-800 flex items-center justify-center">
            <Monitor size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-white">Device Management</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              Provisioning waiting orders, installing + pairing units, boot branding and fleet rollout — one place.
            </p>
          </div>
        </div>

        {/* Tab bar inside header (tenant-page pattern) */}
        <div className="flex items-center gap-1 mt-4 -mb-4 overflow-x-auto pb-0">
          {TABS.filter((t) => !(t.id === 'fleet' && fleetMoved)).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                tab === t.id
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {/* Emerald accent line */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #10b981, #059669, #047857)' }} />

      {/* Light content area */}
      {/* Force a dark default: index.html sets body `text-white`, so every text
          node on these light cards would otherwise inherit white-on-white. */}
      <div className="bg-gray-50 min-h-screen text-gray-900">
        {/* FULL WIDTH, no centred measure. The fleet table carries eleven
            columns and the install lane is just as wide — capping this at
            1200px pushed both into a horizontal scrollbar while the screen sat
            half empty. Text that needs a readable measure sets its own (the
            Pending tab's intro is `max-w-[68ch]`). */}
        <main className="w-full p-4 md:p-6 lg:p-8">
          {tab === 'pending' && <PendingConfigurationTab />}
          {tab === 'fleet'
            ? <LegacyFleetGate><BrandingTab tab="fleet" /></LegacyFleetGate>
            : BRANDING_TABS.includes(tab as BrandingTabId) && <BrandingTab tab={tab as BrandingTabId} />}
        </main>
      </div>
    </div>
  );
};

export default PosConfigTab;
